import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import gotchiIcon from '@assets/images/characters/gotchi.png'
import './GotchiRainCelebration.scss'

/** New drops keep starting during this window (continuous rain). */
const SPAWN_MS = 1800
/** Longest single fall — after spawn ends, remaining drops finish exiting. */
const MAX_FALL_MS = 1100
const TINY_COUNT = 120
const BIG_COUNT = 6

type Drop = {
  id: number
  kind: 'tiny' | 'big'
  leftPct: number
  sizePx: number
  durationMs: number
  delayMs: number
  rotateDeg: number
  driftPx: number
  opacity: number
}

type Props = {
  active: boolean
  onDone?: () => void
}

function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function buildDrops(): Drop[] {
  const tiny: Drop[] = Array.from({ length: TINY_COUNT }, (_, i) => {
    const durationMs = 700 + Math.floor(Math.random() * 400) // 700–1100
    // Stagger starts across the spawn window so rain feels continuous,
    // then every drop completes one full descent (no abrupt cut).
    const delayMs = Math.floor((i / TINY_COUNT) * SPAWN_MS + Math.random() * 40)
    return {
      id: i,
      kind: 'tiny' as const,
      leftPct: Math.random() * 100,
      sizePx: 8 + Math.floor(Math.random() * 9),
      durationMs,
      delayMs: Math.min(delayMs, SPAWN_MS),
      rotateDeg: 4 + Math.floor(Math.random() * 16),
      driftPx: -18 + Math.floor(Math.random() * 36),
      opacity: 0.45 + Math.random() * 0.35,
    }
  })

  const big: Drop[] = Array.from({ length: BIG_COUNT }, (_, i) => ({
    id: TINY_COUNT + i,
    kind: 'big' as const,
    leftPct: 8 + Math.random() * 84,
    sizePx: 34 + Math.floor(Math.random() * 34),
    durationMs: 1000 + Math.floor(Math.random() * Math.min(MAX_FALL_MS - 200, 550)),
    delayMs: Math.floor(Math.random() * Math.min(420, SPAWN_MS * 0.4)),
    rotateDeg: 8 + Math.floor(Math.random() * 18),
    driftPx: -28 + Math.floor(Math.random() * 56),
    opacity: 1,
  }))

  return [...tiny, ...big]
}

/**
 * Continuous gotchi rain: new drops spawn for ~1.8s, then all finish falling off-screen.
 */
export function GotchiRainCelebration({ active, onDone }: Props) {
  const [visible, setVisible] = useState(false)
  const [drops, setDrops] = useState<Drop[]>([])

  useEffect(() => {
    if (!active) {
      setVisible(false)
      return
    }
    if (prefersReducedMotion()) {
      onDone?.()
      return
    }
    setDrops(buildDrops())
    setVisible(true)
    // Stay mounted until the last delayed drop has fully exited.
    const t = window.setTimeout(() => {
      setVisible(false)
      onDone?.()
    }, SPAWN_MS + MAX_FALL_MS)
    return () => window.clearTimeout(t)
  }, [active, onDone])

  const root = useMemo(() => {
    if (typeof document === 'undefined') return null
    return document.querySelector('.AppLayout') ?? document.body
  }, [visible])

  if (!visible || !root) return null

  return createPortal(
    <div className="GotchiRainCelebration" aria-hidden>
      {drops.map((d) => (
        <img
          key={d.id}
          src={gotchiIcon}
          alt=""
          className={`GotchiRainCelebration-gotchi GotchiRainCelebration-gotchi--${d.kind}`}
          style={
            {
              left: `${d.leftPct}%`,
              width: d.sizePx,
              height: d.sizePx,
              opacity: d.opacity,
              '--gr-duration': `${d.durationMs}ms`,
              '--gr-delay': `${d.delayMs}ms`,
              '--gr-rotate': `${d.rotateDeg}deg`,
              '--gr-drift': `${d.driftPx}px`,
            } as React.CSSProperties
          }
        />
      ))}
    </div>,
    root,
  )
}

export default GotchiRainCelebration
